import { Navigate, Outlet } from "react-router-dom";
import { useRecoilValue } from "recoil";

import { menuItemsAtom, Roles, useHasRole } from "./AuthProvider";
import { RevbellMenuItem } from "./Layout/Menu/Menu";

const hasModuleAccess = (
  routeName: string,
  menus: RevbellMenuItem[],
  hasAnyRole: (roles: Roles[]) => boolean
): boolean => {
  const currMenu = menus
    ? menus.find((menu: RevbellMenuItem) => menu.name === routeName)
    : false;

  if (!currMenu) return false;
  return hasAnyRole(currMenu.allowedRoles);
};

const findFirstAccessibleModule = (
  menus: RevbellMenuItem[],
  hasAnyRole: (roles: Roles[]) => boolean
): RevbellMenuItem | null => {
  const firstAccessibleModule = menus.find((menu: RevbellMenuItem) =>
    hasAnyRole(menu.allowedRoles)
  );

  return firstAccessibleModule || null;
};

type ProtectedRouteProps =
  | { routeName: string; requiredRole?: never }
  | { requiredRole: Roles; routeName?: never };

const ProtectedRoute = ({ routeName, requiredRole }: ProtectedRouteProps) => {
  const { hasAnyRole, hasRole } = useHasRole();
  const menuItems = useRecoilValue(menuItemsAtom);

  const isAllowed = requiredRole
    ? hasRole(requiredRole)
    : hasModuleAccess(routeName, menuItems, hasAnyRole);

  if (!isAllowed) {
    const firstAccessibleModule = findFirstAccessibleModule(
      menuItems,
      hasAnyRole
    );

    if (firstAccessibleModule) {
      console.error(
        `Redirection ! You don't have access to this page: ${
          routeName ?? requiredRole
        }`
      );
      return <Navigate to={firstAccessibleModule.reactRoute} replace />;
    } else {
      console.error("No accessible page");
    }
  }

  return <Outlet />;
};

export default ProtectedRoute;
